const scorecard = `<h2>BlueBear Enterprise Platform Evidence Scorecard</h2>

<p>Use this weighted scorecard against one real workflow. A vendor earns points only when the review team can inspect the named evidence; a slide or unchecked product claim earns zero. The weights are a starting template, not a universal procurement standard.</p>

<table>
  <thead><tr><th>Evaluation area</th><th>Weight</th><th>Evidence to inspect in BlueBear</th><th>Fail condition</th></tr></thead>
  <tbody>
    <tr><td>Identity and isolation</td><td>20%</td><td>Tenant, organization, workspace, user or service identity, and effective access rights for the run.</td><td>The acting identity or workspace boundary cannot be reconstructed.</td></tr>
    <tr><td>MCP and integration control</td><td>20%</td><td>Assigned connection, permitted tool/action, credential boundary, policy result, and approval state.</td><td>A model receives reusable credentials or can invoke an unassigned tool.</td></tr>
    <tr><td>Runtime and deployment</td><td>15%</td><td>Selected deployment boundary, workload status, quota state, and retained execution result.</td><td>The production runtime or customer-cloud responsibility is ambiguous.</td></tr>
    <tr><td>Observability and replay</td><td>15%</td><td>Correlated session, model, tool call, retry, approval, failure, and outcome identifiers.</td><td>Operators must manually join unrelated logs to explain a run.</td></tr>
    <tr><td>Cost and routing</td><td>15%</td><td>Effective model policy, token and tool usage, estimated cost, retry cost, and cost per accepted outcome.</td><td>Only a provider invoice or aggregate token total is available.</td></tr>
    <tr><td>Operations and ownership</td><td>15%</td><td>Named workflow owner, exception queue, incident path, approval authority, and retirement procedure.</td><td>No accountable owner can stop or change the workflow.</td></tr>
  </tbody>
</table>

<h3>How to run the evaluation</h3>
<ol>
  <li>Select one workflow with an owner, input, tool action, acceptance rule, and measurable outcome.</li>
  <li>Run a normal case, a denied action, a tool failure, a retry, and a human-approval case.</li>
  <li>Score only the evidence the review team can inspect from those runs.</li>
  <li>Record missing evidence as a deployment risk, not as a feature-roadmap promise.</li>
</ol>`;

const businessCase = `<h2>BlueBear Outcome-Cost Decision Worksheet</h2>

<p>BlueBear treats the business case as an operational measurement problem. The platform can correlate model and tool activity with sessions, retries, review, and outcomes; the buyer still owns the labor rate, value assumptions, and approval threshold.</p>

<pre><code>cost per accepted outcome =
  (model + tool + runtime + retry + review + exception cost)
  / accepted outcomes

net value per accepted outcome =
  verified business value - cost per accepted outcome</code></pre>

<table>
  <thead><tr><th>Worksheet field</th><th>BlueBear evidence</th><th>Business-owned input</th></tr></thead>
  <tbody>
    <tr><td>Attempt volume and completion</td><td>Sessions, workflow status, retries, failures, and accepted outcomes.</td><td>What counts as a completed and accepted business result.</td></tr>
    <tr><td>Technical consumption</td><td>Model, token, tool, runtime, and estimated cost records attached to the work.</td><td>Approved rate source and accounting period.</td></tr>
    <tr><td>Human effort</td><td>Approval and exception events with elapsed time where available.</td><td>Loaded labor rate and which review steps are incremental.</td></tr>
    <tr><td>Business value</td><td>Outcome identifier that can be reconciled with the operating system of record.</td><td>Revenue, loss avoidance, cycle-time, or capacity value and confidence range.</td></tr>
  </tbody>
</table>

<p>Keep a low, expected, and high case. Do not convert unverified time savings into booked ROI, and do not omit rejected outputs from the denominator.</p>`;

const healthcareQueue = `<h2>BlueBear Healthcare Exception State Contract</h2>

<p>BlueBear models exception handling as an explicit workflow with ownership and evidence. The exact clinical or operational decision remains with the customer; the platform provides the queue, policy, approval, integration, and audit boundaries around that decision.</p>

<table>
  <thead><tr><th>State</th><th>Entry condition</th><th>Required owner/evidence</th><th>Allowed exit</th></tr></thead>
  <tbody>
    <tr><td>Received</td><td>A request or document enters the governed workspace.</td><td>Tenant, workspace, source, timestamp, and correlation ID.</td><td>Validated or quarantined.</td></tr>
    <tr><td>Needs review</td><td>Required data is missing, confidence is below policy, or a rule requires human judgment.</td><td>Reason code, extracted fields, source reference, and named queue.</td><td>Approved, corrected, rejected, or escalated.</td></tr>
    <tr><td>Waiting on dependency</td><td>A signature, authorization, external system, or customer response is outstanding.</td><td>Dependency, due time, retry policy, and current owner.</td><td>Needs review, ready to sync, or expired.</td></tr>
    <tr><td>Ready to sync</td><td>Required checks and approvals are complete.</td><td>Effective approval, destination, permitted action, and credential boundary.</td><td>Completed or sync repair.</td></tr>
    <tr><td>Sync repair</td><td>The downstream write failed or produced an ambiguous result.</td><td>Tool receipt, failure class, retry count, and reconciliation owner.</td><td>Completed, needs review, or escalated.</td></tr>
    <tr><td>Completed</td><td>The downstream system confirms the accepted outcome.</td><td>Outcome ID, completion time, and retained evidence packet.</td><td>Reopened only through a recorded action.</td></tr>
  </tbody>
</table>

<p>For healthcare deployments, customers must confirm role design, disclosure rules, retention, and regulatory requirements for their environment. BlueBear should not be treated as making a clinical decision merely because it coordinates the workflow.</p>`;

const sessionReplay = `<h2>What BlueBear Preserves for Session Reconstruction</h2>

<p>A useful replay is not a screen recording. BlueBear correlates the state required to answer who requested the work, which policy applied, what the model and tools did, where the run changed state, and which outcome was accepted. The table below is the evidence contract; it is not presented as a sanitized customer trace.</p>

<table>
  <thead><tr><th>Evidence group</th><th>Fields to preserve</th><th>Incident question answered</th></tr></thead>
  <tbody>
    <tr><td>Request context</td><td>Session, tenant, workspace, actor, agent, workflow version, and correlation ID.</td><td>Whose boundary and which version produced the run?</td></tr>
    <tr><td>Policy and routing</td><td>Effective model policy, eligible route, exclusions, fallback rule, and policy result.</td><td>Why was this model or fallback allowed?</td></tr>
    <tr><td>Tool execution</td><td>MCP connection, tool/action, credential reference, request receipt, status, and result metadata.</td><td>What external action ran, and under which authority?</td></tr>
    <tr><td>Human control</td><td>Approval requirement, reviewer, decision, reason, and elapsed time.</td><td>Where did a person permit, change, or reject the work?</td></tr>
    <tr><td>Economics and outcome</td><td>Tokens, estimated cost, retries, terminal state, accepted outcome, and downstream identifier.</td><td>What did the incident cost and did it create a valid result?</td></tr>
  </tbody>
</table>

<p>A customer-safe annotated trace should be published only after source selection and redaction approval. Until then, this explicit contract is more trustworthy than presenting invented production telemetry.</p>`;

const proofOfValue = `<h2>BlueBear Production Review Evidence Pack</h2>

<p>A BlueBear proof of value ends with an evidence pack, not a demo recording. The workflow owner should be able to accept or reject production rollout from the artifacts below.</p>

<table>
  <thead><tr><th>Gate</th><th>Evidence pack item</th><th>Decision question</th></tr></thead>
  <tbody>
    <tr><td>Outcome</td><td>Accepted-output definition, sample size, completion rate, rejection reasons, and downstream reconciliation.</td><td>Did the workflow create the result the business agreed to measure?</td></tr>
    <tr><td>Control</td><td>Identity, workspace, MCP tool scope, approval path, credential boundary, and denied-action test.</td><td>Can the workflow act only inside its approved boundary?</td></tr>
    <tr><td>Reliability</td><td>Normal, degraded, tool-failure, retry, and recovery runs with correlated session evidence.</td><td>Can operators explain and recover the failure modes?</td></tr>
    <tr><td>Economics</td><td>Model, tool, runtime, retry, exception, and review cost per accepted outcome.</td><td>Are unit economics viable at expected volume?</td></tr>
    <tr><td>Operations</td><td>Named owner, exception queue, incident runbook, budget decision, and retirement path.</td><td>Who operates, changes, stops, and pays for the workflow?</td></tr>
  </tbody>
</table>

<p>BlueBear supplies the governed workspace and correlated operational evidence. The customer approves acceptance criteria, risk tier, accountable owners, and the production decision.</p>`;

const costBaseline = `<h2>BlueBear Cost-per-Outcome Baseline Worksheet</h2>

<p>BlueBear's Sessions and Budget Manager surfaces make technical consumption inspectable, but a defensible baseline must also include retries, exceptions, and human review. Use actual approved rate inputs; the example formula contains no claimed customer measurements.</p>

<figure>
  <img src="/demo-media/screenshots/usage-controls-screenshot.webp" alt="Sanitized BlueBear Sessions view with token, tool-call, and estimated cost totals.">
  <figcaption>Sanitized first-party BlueBear product evidence: session-level usage is an input to the baseline, not a substitute for cost per accepted outcome.</figcaption>
</figure>

<pre><code>workflow cost =
  model cost
  + tool/API cost
  + allocated runtime cost
  + retry cost
  + human review cost
  + exception-resolution cost

cost per accepted outcome = workflow cost / accepted outcomes</code></pre>

<table>
  <thead><tr><th>Measure</th><th>Collection rule</th><th>Common distortion</th></tr></thead>
  <tbody>
    <tr><td>Attempts</td><td>Count every initiated session for the workflow version and period.</td><td>Counting only successful model calls.</td></tr>
    <tr><td>Retries and fallbacks</td><td>Attach each retry and fallback to its originating session and reason.</td><td>Blending recovery spend into an aggregate invoice.</td></tr>
    <tr><td>Review and exceptions</td><td>Record approval or queue elapsed time and multiply only active labor by an approved rate.</td><td>Treating all elapsed queue time as labor, or ignoring labor entirely.</td></tr>
    <tr><td>Accepted outcomes</td><td>Reconcile terminal BlueBear state with the business system of record.</td><td>Using agent completion as a proxy for business acceptance.</td></tr>
    <tr><td>Allocation</td><td>Apply the same documented runtime-allocation rule across comparison periods.</td><td>Changing the allocation method after an optimization.</td></tr>
  </tbody>
</table>

<p>Publish the measurement window, workflow version, sample size, rate source, exclusions, and confidence range beside every baseline so another reviewer can reproduce the result.</p>`;

const mcpTokenBoundary = `<h2>BlueBear MCP Authorization Evidence Contract</h2>

<p>The MCP specification prohibits token passthrough because a server accepting a token that was not issued for it breaks audience validation and the authorization boundary. In a BlueBear architecture review, this becomes a testable execution contract rather than a reminder to “secure the token.”</p>

<table>
  <thead><tr><th>Decision point</th><th>Required evidence</th><th>Failure test</th></tr></thead>
  <tbody>
    <tr><td>Issuer and subject</td><td>Trusted issuer, authenticated human or service subject, tenant, and workspace.</td><td>Reject an unknown issuer or subject outside the workspace.</td></tr>
    <tr><td>Audience and resource</td><td>The token audience/resource matches the MCP server or protected resource receiving it.</td><td>Replay a token issued for a different service and confirm denial.</td></tr>
    <tr><td>Capability scope</td><td>Assigned MCP connection, permitted tool, action class, resource scope, and approval requirement.</td><td>Request an unassigned tool or broader action and preserve the denial reason.</td></tr>
    <tr><td>Credential resolution</td><td>Credential reference, expiry, selected connector, and execution receipt; no reusable token in model context.</td><td>Inspect prompts, model logs, and workspace artifacts for bearer material.</td></tr>
    <tr><td>Delegation and outcome</td><td>Correlation from authenticated actor through policy and tool execution to the accepted result.</td><td>Attempt to complete a run when the delegation or approval context is missing.</td></tr>
  </tbody>
</table>

<p><strong>BlueBear boundary:</strong> workspace assignment, policy evaluation, scoped credential resolution, and correlated execution evidence are platform controls where configured. Issuer policy, identity-provider configuration, token lifetime, key ownership, and downstream resource authorization remain deployment-specific and must be verified.</p>`;

const productionGatewayRunbook = `<h2>BlueBear Production MCP Gateway Acceptance Runbook</h2>

<p>A gateway is ready only when operators can prove both allowed work and safe failure. Run the cases below against one real MCP-backed workflow before increasing production scope.</p>

<table>
  <thead><tr><th>Test</th><th>Expected behavior</th><th>Evidence to retain</th></tr></thead>
  <tbody>
    <tr><td>Allowed action</td><td>The assigned tool runs with the scoped credential and returns a correlated receipt.</td><td>Actor, workspace, policy, credential reference, tool call, result, and outcome.</td></tr>
    <tr><td>Unassigned tool</td><td>The request is denied before connector execution.</td><td>Requested capability, effective assignments, denial code, and no downstream call.</td></tr>
    <tr><td>Audience mismatch</td><td>A token or credential intended for another resource is rejected.</td><td>Issuer/audience validation result without exposing secret material.</td></tr>
    <tr><td>High-impact action</td><td>Execution pauses for the named approval authority; expired or changed approvals fail closed.</td><td>Action fingerprint, reviewer, decision, reason, expiry, and post-approval receipt.</td></tr>
    <tr><td>Timeout and retry</td><td>Retries follow the declared policy, avoid duplicate side effects, and preserve the originating request.</td><td>Attempt numbers, idempotency key, tool statuses, latency, and terminal state.</td></tr>
    <tr><td>Emergency containment</td><td>Operators can disable a connection or capability and identify affected sessions.</td><td>Control change, actor, time, impacted workflows, and recovery approval.</td></tr>
  </tbody>
</table>

<p>BlueBear can provide the workspace, policy, approval, credential, and session evidence around the gateway. The customer still owns tool risk classification, downstream authorization, retention, incident command, and the final production acceptance decision.</p>`;

const workloadIdentityManifest = `<h2>BlueBear Agent Workload Identity Manifest</h2>

<p>Replace the idea of “the agent credential” with a versioned manifest that states who delegated the work, where it runs, what it may request, and how its authority expires.</p>

<pre><code>{
  "tenant": "tenant-id",
  "workspace": "workspace-id",
  "agent": "agent-definition-id",
  "workflowVersion": "approved-version",
  "subjectType": "service-principal",
  "allowedConnections": ["approved-mcp-connection"],
  "allowedActions": ["read:case", "propose:update"],
  "approvalRequired": ["execute:update"],
  "credentialMode": "resolve-at-execution",
  "expiresAt": "bounded-timestamp"
}</code></pre>

<table>
  <thead><tr><th>Lifecycle event</th><th>Control</th><th>Evidence</th></tr></thead>
  <tbody>
    <tr><td>Issue</td><td>Bind the workload to tenant, workspace, approved agent/workflow version, and runtime.</td><td>Issuer, subject, audience, version, owner, and expiry.</td></tr>
    <tr><td>Use</td><td>Authorize the requested MCP capability and resource at execution time.</td><td>Effective policy, connection, tool/action, credential reference, and result.</td></tr>
    <tr><td>Change</td><td>Require review when tools, actions, runtime, workflow version, or delegation scope changes.</td><td>Previous and new manifest, approver, reason, and activation time.</td></tr>
    <tr><td>Revoke</td><td>Disable the workload identity and dependent capabilities without editing prompts.</td><td>Revocation actor, time, affected sessions, and containment confirmation.</td></tr>
  </tbody>
</table>`;

const finopsAllocation = `<h2>BlueBear AI FinOps Allocation Ledger</h2>

<p>The FinOps Foundation reports that AI spend management is now widespread and a leading priority. For agents, a provider invoice is still only one ledger input. BlueBear’s useful allocation grain is the governed workflow and its accepted outcome.</p>

<table>
  <thead><tr><th>Cost pool</th><th>Allocation key</th><th>BlueBear evidence</th><th>Owner input</th></tr></thead>
  <tbody>
    <tr><td>Model</td><td>Request or session usage</td><td>Model route, tokens, estimated rate, retries, and fallback.</td><td>Approved rate table and accounting period.</td></tr>
    <tr><td>Tool/API</td><td>Tool invocation</td><td>MCP connection, action, count, receipt, and originating session.</td><td>Vendor rate and free-tier treatment.</td></tr>
    <tr><td>Runtime</td><td>Documented workload allocation</td><td>Workspace, deployment, execution time, and capacity metadata where available.</td><td>Allocation method for shared capacity.</td></tr>
    <tr><td>Human review</td><td>Active handling time</td><td>Approval and exception events, owner, status, and elapsed time.</td><td>Active-time rule and loaded labor rate.</td></tr>
    <tr><td>Business result</td><td>Accepted outcome</td><td>Terminal workflow state and downstream outcome identifier.</td><td>Acceptance definition, value, and reconciliation source.</td></tr>
  </tbody>
</table>

<p>Report attempts, accepted outcomes, acceptance rate, cost per attempt, and cost per accepted outcome together. A lower model bill is not an optimization if rejection, review, latency, or exception costs rise enough to damage the business result.</p>

<p><a href="/ai-agent-cost-per-outcome-calculator">Use the BlueBear cost-per-outcome calculator</a> to challenge an allocation before publishing it.</p>`;

const productionGate = `<h2>BlueBear Demo-to-Production Release Gate</h2>

<p>A persuasive demo proves that a happy path can work. A production release decision needs evidence that the workflow remains bounded, recoverable, affordable, and owned when the happy path stops. BlueBear uses the following gate against one versioned workflow.</p>

<table>
  <thead><tr><th>Gate</th><th>Pass evidence</th><th>Stop condition</th></tr></thead>
  <tbody>
    <tr><td>Outcome</td><td>Accepted-output definition, sample window, rejection reasons, and downstream reconciliation.</td><td>Agent completion is treated as business acceptance.</td></tr>
    <tr><td>Authority</td><td>Actor, tenant, workspace, tool/action scope, credential boundary, and denied-action test.</td><td>The agent can expand scope through prompt instructions.</td></tr>
    <tr><td>Failure</td><td>Timeout, retry, duplicate-side-effect, approval expiry, and downstream repair tests.</td><td>No owner or safe terminal state exists for an exception.</td></tr>
    <tr><td>Economics</td><td>Model, tool, runtime, retry, review, and exception cost per accepted outcome.</td><td>Only provider-call cost is measured.</td></tr>
    <tr><td>Operations</td><td>Named service owner, queue owner, incident path, budget authority, and retirement procedure.</td><td>A committee is accountable but no person can stop the workflow.</td></tr>
  </tbody>
</table>

<p>The gate is deliberately binary for release: missing evidence is a production risk, not a feature that can be inferred from the demo.</p>`;

const frameworkDecision = `<h2>BlueBear Framework-versus-Platform Decision Record</h2>

<p>The useful decision is not whether a framework or platform is “better.” Record which production responsibilities the application team will own directly and which require a shared operating boundary.</p>

<table>
  <thead><tr><th>Responsibility</th><th>Framework-led path</th><th>BlueBear platform path</th><th>Decision evidence</th></tr></thead>
  <tbody>
    <tr><td>Agent logic</td><td>Application team selects libraries and implements orchestration.</td><td>Agent definitions run inside governed workspaces and workflows.</td><td>Version, test result, and accountable application owner.</td></tr>
    <tr><td>Tool authority</td><td>Team builds credential, scope, approval, and audit controls.</td><td>MCP assignments, action policy, credential resolution, and approvals share an execution boundary.</td><td>Allowed and denied tool-action traces.</td></tr>
    <tr><td>Model policy</td><td>Team implements eligibility, routing, fallback, and cost attribution.</td><td>Routing policy and session evidence connect choice, usage, fallback, and outcome.</td><td>Effective policy and route explanation.</td></tr>
    <tr><td>Multi-tenant operations</td><td>Team owns isolation, roles, quotas, evidence, and lifecycle.</td><td>Tenant and workspace boundaries organize users, agents, integrations, budgets, and sessions.</td><td>Boundary tests and responsibility matrix.</td></tr>
    <tr><td>Incident reconstruction</td><td>Team correlates application, model, connector, approval, and infrastructure logs.</td><td>BlueBear preserves correlated workflow evidence where the capabilities are enabled.</td><td>Reconstructed failure timeline and containment action.</td></tr>
  </tbody>
</table>

<p>A framework remains appropriate when the team intentionally owns these controls. A platform becomes valuable when multiple workflows or teams need the same policy, evidence, and lifecycle conventions.</p>`;

const integrationCatalogContract = `<h2>BlueBear Governed Integration Catalog Contract</h2>

<p>A catalog entry should describe an authorization boundary, not just display a connector logo. BlueBear separates what may be discovered from what may be assigned and executed.</p>

<pre><code>{
  "connection": "approved-connection-id",
  "tenant": "owning-tenant",
  "workspaceAssignments": ["approved-workspace"],
  "server": "mcp-server-id",
  "allowedTools": ["read_record", "propose_update"],
  "approvalRequired": ["execute_update"],
  "credentialMode": "resolve-at-execution",
  "dataClass": "customer-defined",
  "owner": "integration-owner",
  "reviewBy": "bounded-date"
}</code></pre>

<table>
  <thead><tr><th>Lifecycle step</th><th>Required decision</th><th>Evidence</th></tr></thead>
  <tbody>
    <tr><td>Admit</td><td>Approve the server, owner, trust boundary, tools, data class, and deployment path.</td><td>Review record, version, source, and test result.</td></tr>
    <tr><td>Assign</td><td>Choose the tenant/workspace, permitted actions, approvers, and budget boundary.</td><td>Effective assignment and policy.</td></tr>
    <tr><td>Execute</td><td>Resolve identity and credential only after the requested capability is authorized.</td><td>Policy result, credential reference, tool receipt, and outcome.</td></tr>
    <tr><td>Change</td><td>Re-review new tools, scopes, side effects, versions, or data flows.</td><td>Diff, approver, activation time, and rollback plan.</td></tr>
    <tr><td>Revoke</td><td>Disable the connection or assignment and identify dependent workflows.</td><td>Revocation event, affected sessions, owner, and completion state.</td></tr>
  </tbody>
</table>`;

const reliabilityPacket = `<h2>BlueBear Reliability Evidence Packet</h2>

<p>Reliability is the ability to explain and recover a workflow outcome, not a single aggregate accuracy number. BlueBear correlates the following evidence where each capability is enabled; the customer defines the acceptance and service thresholds.</p>

<figure>
  <img src="/demo-media/screenshots/usage-controls-screenshot.webp" alt="Sanitized BlueBear session view showing token, tool-call, and estimated cost evidence.">
  <figcaption>Sanitized first-party product evidence: session usage is one layer of a reliability packet and must be joined to policy, tool, approval, failure, and outcome context.</figcaption>
</figure>

<table>
  <thead><tr><th>Layer</th><th>Minimum fields</th><th>Reliability question</th></tr></thead>
  <tbody>
    <tr><td>Request</td><td>Correlation ID, tenant, workspace, actor, agent, and workflow version.</td><td>Which bounded system received the work?</td></tr>
    <tr><td>Decision</td><td>Policy version, eligible route, selected model, exclusions, and fallback rule.</td><td>Why was this execution path allowed?</td></tr>
    <tr><td>Action</td><td>MCP connection, tool/action, approval, idempotency key, attempt, and receipt.</td><td>Did an external side effect occur once, more than once, or not at all?</td></tr>
    <tr><td>Terminal state</td><td>Success, denial, timeout, repair, rejection, or accepted outcome with downstream ID.</td><td>Did the workflow create a valid business result?</td></tr>
    <tr><td>Economics</td><td>Latency, tokens, tool/runtime cost, retries, review, and exception handling.</td><td>Was recovery reliable at an acceptable operating cost?</td></tr>
  </tbody>
</table>`;

const tenantOnboarding = `<h2>BlueBear White-Label Tenant Activation Record</h2>

<p>Tenant onboarding is complete only when the brand, authority, deployment, economics, support ownership, and first workflow have an approved effective state.</p>

<table>
  <thead><tr><th>Activation area</th><th>BlueBear configuration/evidence</th><th>Customer-owned decision</th></tr></thead>
  <tbody>
    <tr><td>Tenant identity</td><td>Tenant ID, domain/brand configuration, administrators, and activation status.</td><td>Authorized administrators and customer-facing brand approval.</td></tr>
    <tr><td>Workspace access</td><td>Workspace membership, roles, agent assignments, MCP connections, and denied-action test.</td><td>User population, least-privilege design, and approval authorities.</td></tr>
    <tr><td>Deployment boundary</td><td>Managed or customer-cloud target, runtime status, and responsibility record.</td><td>Network, keys, retention, region, and incident requirements.</td></tr>
    <tr><td>Usage and budget</td><td>Tenant/workspace budget scope, model access, usage evidence, and notification path.</td><td>Budget owner, limits, rates, and exception authority.</td></tr>
    <tr><td>First workflow</td><td>Version, tools, approvals, normal/denied/failure tests, and accepted outcome.</td><td>Workflow owner, acceptance criteria, service target, and launch approval.</td></tr>
    <tr><td>Support and exit</td><td>Escalation contacts, export/retention state, disabled routes, and revocation evidence.</td><td>Support model, communications, contractual obligations, and offboarding approval.</td></tr>
  </tbody>
</table>

<p>Do not activate a tenant with unresolved ownership. BlueBear supplies platform controls and operating evidence where configured; the operator remains responsible for customer promises, deployment choices, user authorization, and launch acceptance.</p>`;

const whiteLabelEconomics = `<h2>BlueBear White-Label Unit-Economics Worksheet</h2>
<p>White-label margin must be measured per tenant and accepted workflow outcome, not inferred from the model invoice. Use the same allocation rule every period.</p>
<table><thead><tr><th>Ledger line</th><th>Allocation basis</th><th>Decision</th></tr></thead><tbody>
<tr><td>Contracted revenue</td><td>Tenant subscription plus approved usage revenue.</td><td>Separate recurring, variable, and one-time revenue.</td></tr>
<tr><td>Model, tool, and runtime</td><td>Correlated tenant/workspace sessions and documented shared-capacity allocation.</td><td>Identify workflows whose technical cost exceeds their value tier.</td></tr>
<tr><td>Review, exception, and support</td><td>Active handling time at approved loaded rates.</td><td>Expose tenants or workflows creating operational drag.</td></tr>
<tr><td>Onboarding and change cost</td><td>Amortized implementation effort with the period and rule disclosed.</td><td>Distinguish launch investment from steady-state margin.</td></tr>
<tr><td>Accepted outcomes</td><td>Reconciled workflow results, including rejection and rework.</td><td>Compare margin per accepted outcome, not cost per call.</td></tr>
</tbody></table>
<p><a href="/ai-agent-cost-per-outcome-calculator">Model the workflow cost</a>, then attach tenant revenue and support assumptions outside the calculator. BlueBear does not infer customer margin from platform telemetry alone.</p>`;

const controlPlaneLifecycle = `<h2>BlueBear Control-Plane Request Lifecycle</h2>
<p>A control plane is useful when it makes the effective decision inspectable. This lifecycle separates configured intent from runtime execution.</p>
<table><thead><tr><th>Stage</th><th>Control-plane decision</th><th>Evidence</th></tr></thead><tbody>
<tr><td>Resolve context</td><td>Identify tenant, workspace, actor, agent, workflow version, deployment, and budget owner.</td><td>Authenticated subject and effective resource boundary.</td></tr>
<tr><td>Compute policy</td><td>Evaluate roles, model eligibility, MCP assignments, action scope, approvals, quotas, and fallback.</td><td>Policy version, eligible set, exclusions, and allow/deny reason.</td></tr>
<tr><td>Issue execution</td><td>Pass the bounded request and credential reference to runtime without exposing reusable secrets to model context.</td><td>Execution request, credential reference, runtime, and correlation ID.</td></tr>
<tr><td>Reconcile</td><td>Join route, tool attempts, approvals, cost, terminal state, and downstream accepted outcome.</td><td>Session timeline and outcome identifier.</td></tr>
<tr><td>Change or contain</td><td>Version policy changes, disable capabilities, identify affected workflows, and retain the operator decision.</td><td>Change actor, reason, time, impact set, and recovery approval.</td></tr>
</tbody></table>
<p>Deployment compute may remain managed, customer-cloud, or private. The BlueBear value is the shared policy and evidence boundary around those execution environments, not a claim that one control plane owns every underlying system.</p>`;

const byocMatrix = `<h2>BlueBear BYOC Responsibility and Cost Matrix</h2>
<table><thead><tr><th>Area</th><th>Managed path</th><th>Customer-cloud path</th><th>Evidence required</th></tr></thead><tbody>
<tr><td>Cloud account and billing</td><td>Platform-managed account and service boundary.</td><td>Customer account, quotas, commitments, and cloud invoice.</td><td>Account owner, allocation rule, budget, and escalation.</td></tr>
<tr><td>Network and keys</td><td>Defined by the managed service architecture.</td><td>Customer architecture may own network, key, and egress policy.</td><td>Responsibility matrix and validated connectivity.</td></tr>
<tr><td>Runtime operations</td><td>Managed upgrade, capacity, and incident responsibilities.</td><td>Responsibilities split across BlueBear and customer operations.</td><td>Runtime version, health, support path, and rollback owner.</td></tr>
<tr><td>Agent governance</td><td>BlueBear tenant/workspace, model, MCP, budget, and session controls where configured.</td><td>The same logical controls with deployment-specific integrations and assurance.</td><td>Effective policy, denial tests, approvals, and outcome trace.</td></tr>
<tr><td>Assurance</td><td>Request current service evidence and agreements.</td><td>Combine BlueBear evidence with customer cloud controls.</td><td>Current documents; never infer certification from hosting choice.</td></tr>
</tbody></table>`;

const readinessRecord = `<h2>BlueBear Production Readiness Decision Record</h2>
<table><thead><tr><th>Decision</th><th>Required evidence</th><th>Accountable owner</th></tr></thead><tbody>
<tr><td>Release scope</td><td>Versioned workflow, input boundary, tools, models, users, and accepted outcome.</td><td>Business workflow owner.</td></tr>
<tr><td>Security boundary</td><td>Identity, workspace, action policy, credential resolution, approval, and denial tests.</td><td>Security owner.</td></tr>
<tr><td>Reliability</td><td>Normal, timeout, retry, duplicate, downstream failure, repair, and containment results.</td><td>Service owner.</td></tr>
<tr><td>Economics</td><td>Cost per accepted outcome with model, tool, runtime, review, and exception inputs.</td><td>Budget owner.</td></tr>
<tr><td>Operate or stop</td><td>Monitoring, queue, incident, change, rollback, retention, and retirement procedures.</td><td>Named operational owner.</td></tr>
</tbody></table>
<p>The signed decision should include gaps, compensating controls, expiration/review date, and the person authorized to stop the workflow. “Pilot succeeded” is not a production approval.</p>`;

const migrationMap = `<h2>BlueBear Pilot-to-Platform Dependency Map</h2>
<table><thead><tr><th>Dependency</th><th>Pilot discovery</th><th>Shared-platform target</th><th>Cutover proof</th></tr></thead><tbody>
<tr><td>Identity and ownership</td><td>Users, service accounts, hidden owners, and manual approvals.</td><td>Named tenant/workspace roles and accountable workflow owner.</td><td>Allowed and denied identity tests.</td></tr>
<tr><td>Models and prompts</td><td>Providers, versions, keys, limits, fallback, and embedded policy.</td><td>Versioned model eligibility and routing policy.</td><td>Shadow comparison and accepted outcome checks.</td></tr>
<tr><td>Tools and data</td><td>Direct APIs, MCP servers, credentials, scopes, and data movement.</td><td>Assigned connections, execution-time credentials, action policy, and approvals.</td><td>Tool receipts, denial tests, and downstream reconciliation.</td></tr>
<tr><td>Runtime and reliability</td><td>Hosting, queues, retries, state, schedules, and manual repair.</td><td>Declared deployment, idempotency, exception states, and incident ownership.</td><td>Failure rehearsal and rollback test.</td></tr>
<tr><td>Cost and value</td><td>Provider spend and unmeasured review/rework.</td><td>Tenant/workflow allocation and cost per accepted outcome.</td><td>Comparable baseline and post-cutover window.</td></tr>
</tbody></table>
<p>Migrate one bounded workflow first. Inventory and shadow measurement can run in parallel, but production control should move only when the dependency row has an owner and cutover evidence.</p>`;

const shadowRoutingContract = `<h2>BlueBear Shadow-Mode Routing Evidence Contract</h2>

<p>A shadow run is only worth what you recorded while it was running. This is the minimum record that makes a shadow dataset answerable, taken from the fields BlueBear's gateway already emits. It is an implementation contract, not a measurement — no figures below are drawn from production traffic.</p>

<table>
  <thead><tr><th>Field</th><th>Why it is load-bearing</th><th>Recoverable afterwards?</th></tr></thead>
  <tbody>
    <tr><td>Planning mode in force (<code>off</code>, <code>shadow</code>, <code>active</code>)</td><td>Without it, a divergence between planned and executed models is uninterpretable.</td><td>No</td></tr>
    <tr><td>The model that would have been chosen, separate from the model that ran</td><td>Two fields, never one. Overwriting the executed model with the recommendation destroys the comparison you are running the experiment to make.</td><td>No</td></tr>
    <tr><td>Router strategy and selection confidence</td><td>A low-confidence recommendation and a high-confidence one are different evidence, and only one of them should be promoted.</td><td>No</td></tr>
    <tr><td>Candidate scores, including the ones not selected</td><td>The rejected candidates and their reasons are how you find out the router never even considered the model you assumed it would.</td><td>No</td></tr>
    <tr><td>Cost projection with its derivation source</td><td>A projection built from an explicit token demand is defensible; one built from a message-size heuristic needs a wider interval. Blending them silently overstates confidence.</td><td>No</td></tr>
    <tr><td>Fallback reason when no recommendation was produced</td><td>A shadow dataset computed over only the requests that produced a plan is computed over the easy requests.</td><td>No</td></tr>
    <tr><td>Whether a canary executed</td><td>Shadow mode is where the canary lives. A canary execution is a real change to a real user and must never be counted as a shadow observation.</td><td>No</td></tr>
  </tbody>
</table>

<h3>Illustrative candidate record</h3>
<p>The field names below are the real ones from <code>RoutePlanCandidateScore</code>. The values are an example for review; they are not production measurements.</p>
<pre><code>{
  "profile": "chat-default",
  "model": "example-small",
  "backend": "remote",
  "selected": true,
  "eligible": true,
  "confidence": 0.86,
  "estimatedCostUsd": 0.0041,
  "estimateSource": "observed",
  "costProjection": {
    "source": "explicit_token_demand",
    "estimatedInputTokens": 3200,
    "estimatedOutputTokens": 380,
    "inputCostPer1m": 0.0,
    "outputCostPer1m": 0.0,
    "calibration": {
      "scope": "tenant_model_task",
      "sampleCount": 0,
      "inputTokenMultiplier": 1.0,
      "outputTokenMultiplier": 1.0
    }
  },
  "reasons": ["eligible", "within_cost_ceiling"]
}</code></pre>
<p>Prices are held per million tokens with the unit as an explicit field rather than a convention, so nothing downstream has to infer whether a number is per-1K or per-1M. Zero values above are placeholders, not a claim about any model's price.</p>

<h3>Two invariants worth enforcing</h3>
<ol>
  <li><strong>Exactly one candidate may be marked selected</strong>, and its confidence must equal the plan's selection confidence. A plan with two selected candidates, or a mismatch between the two numbers, is a malformed plan and should be rejected rather than recorded.</li>
  <li><strong>A recommendation must be re-validated against the live catalog before it is used.</strong> A plan built from a stale snapshot can name a model that is no longer active, no longer available to the workspace, or has no usable credential. Rejecting late is better than executing a model your policy no longer permits.</li>
</ol>`;

const planHonouredContract = `<h2>BlueBear Route Outcome Plan-Honoured Contract</h2>

<p>An outcome record is what turns routing from an opinion into an audit trail. This is the payload BlueBear's gateway emits after a routed request, with the field names it actually uses. The values are an illustrative example for review; they are not production measurements.</p>

<pre><code>{
  "schemaVersion": "bluebear-route-outcome/v1",
  "outcomeId": "example-outcome-id",
  "requestId": "example-request-id",
  "routePlanId": "example-plan-id",
  "tenantId": "example-tenant",
  "selectedModel": "example-small",
  "executedModel": "example-large",
  "routePlanningMode": "active",
  "canaryExecuted": false,
  "executionSucceeded": true,
  "routerConfidence": 0.86,
  "qualityStatus": "accepted",
  "observedLatencyMs": 0,
  "observedCostUsd": 0.0,
  "inputTokens": 0,
  "outputTokens": 0,
  "userRegenerated": false,
  "toolCallFailed": false,
  "planHonoured": { "honoured": false, "reason": "model_not_executable" }
}</code></pre>

<p>Read the example carefully: <code>selectedModel</code> and <code>executedModel</code> differ, and <code>planHonoured.honoured</code> is <code>false</code> with a stated reason. That is the entire point of the contract. Without the last field this record is indistinguishable from one where the plan was followed, and any savings model built over it will count a refusal as an acceptance.</p>

<h3>The declared refusal reasons</h3>
<p>These are the literal values in <code>ROUTE_OUTCOME_PLAN_HONOURED_REASONS</code>. They are quoted rather than glossed: the codes carry no in-repository definitions, and inventing meanings for them would be exactly the kind of confident guess this contract exists to prevent.</p>
<pre><code>not_active
model_deprecated
model_not_executable
already_requested
no_selected_model
execution_disabled
provider_unavailable</code></pre>

<h3>Parity classification</h3>
<table>
  <thead><tr><th>Classification</th><th>Attribute savings to routing?</th><th>Action</th></tr></thead>
  <tbody>
    <tr><td><code>exact_match</code></td><td>Yes — this population only</td><td>This is your realised saving.</td></tr>
    <tr><td><code>expected_shadow_mismatch</code></td><td>No</td><td>Shadow mode working as designed.</td></tr>
    <tr><td><code>expected_evaluation_mismatch</code></td><td>No</td><td>Keep the evaluation pair identifier so baseline and recommendation stay joinable.</td></tr>
    <tr><td><code>expected_fallback_mismatch</code></td><td>No</td><td>Track the reason mix. A rising <code>provider_unavailable</code> share is an incident forming.</td></tr>
    <tr><td><code>unexpected_mismatch</code></td><td>No</td><td>Alarm. BlueBear's gateway refuses to forward these at all rather than admit an uninterpretable record.</td></tr>
  </tbody>
</table>

<h3>Three numbers to report, not one</h3>
<ol>
  <li><strong>Projected saving</strong> — over every request that produced a recommendation, assuming all were followed.</li>
  <li><strong>Realised saving</strong> — over <code>exact_match</code> outcomes only.</li>
  <li><strong>Recoverable saving</strong> — the difference, broken down by refusal reason, which is the engineering roadmap.</li>
</ol>
<p>Reporting all three is also more persuasive than reporting one. A proposal that says "projected 34%, realised 19%, and here is where the other 15 points went" survives scrutiny in a way a single percentage does not.</p>`;

export const ARTICLE_EVIDENCE_MODULES = {
  'ai-agent-platform-evaluation-scorecard': {
    heading: 'BlueBear Enterprise Platform Evidence Scorecard',
    before: '<h2>An Enterprise AI Agent Platform Evaluation Scorecard</h2>',
    html: scorecard,
  },
  'executive-business-case-ai-agent-platform': {
    heading: 'BlueBear Outcome-Cost Decision Worksheet',
    before: '<h2>Diagnostic Checklist for Your AI Agent Workflows</h2>',
    html: businessCase,
  },
  'healthcare-ai-agent-exception-queues': {
    heading: 'BlueBear Healthcare Exception State Contract',
    before: '<h2>A Diagnostic Checklist for Your AI Agent Workflow</h2>',
    html: healthcareQueue,
  },
  'ai-agent-session-replay-incident-response': {
    heading: 'What BlueBear Preserves for Session Reconstruction',
    before: '<h2>A Practical Diagnostic Checklist for Your AI Agent Workflow</h2>',
    html: sessionReplay,
  },
  'mcp-token-security-no-passthrough': {
    heading: 'BlueBear MCP Authorization Evidence Contract',
    before: '<h2>Practical Diagnostic Checklist',
    html: mcpTokenBoundary,
    citations: ['https://blog.modelcontextprotocol.io/posts/enterprise-managed-auth/'],
  },
  'secure-mcp-gateway-production': {
    heading: 'BlueBear Production MCP Gateway Acceptance Runbook',
    before: '<h2>Practical Diagnostic Checklist',
    html: productionGatewayRunbook,
    citations: ['https://blog.modelcontextprotocol.io/posts/2026-07-28-release-candidate/'],
  },
  'workload-identity-for-ai-agents': {
    heading: 'BlueBear Agent Workload Identity Manifest',
    before: '<h2>Practical Diagnostic Checklist',
    html: workloadIdentityManifest,
    citations: ['https://www.nist.gov/artificial-intelligence/ai-agent-standards-initiative'],
  },
  'ai-finops-cost-allocation-agents': {
    heading: 'BlueBear AI FinOps Allocation Ledger',
    before: '<h2>Practical Diagnostic Checklist',
    html: finopsAllocation,
    citations: ['https://data.finops.org/'],
  },
  'ai-agent-demo-to-production-gap': {
    heading: 'BlueBear Demo-to-Production Release Gate',
    before: '<h2>A Practical Diagnostic Checklist',
    html: productionGate,
  },
  'ai-agent-platform-vs-framework': {
    heading: 'BlueBear Framework-versus-Platform Decision Record',
    before: '<h2>A Practical Diagnostic Checklist',
    html: frameworkDecision,
  },
  'governed-ai-integration-catalog': {
    heading: 'BlueBear Governed Integration Catalog Contract',
    before: '<h2>Practical Diagnostic Checklist',
    html: integrationCatalogContract,
  },
  'ai-agent-reliability-evidence-chain': {
    heading: 'BlueBear Reliability Evidence Packet',
    before: '<h2>Practical Diagnostic Checklist',
    html: reliabilityPacket,
  },
  'white-label-ai-tenant-onboarding-playbook': {
    heading: 'BlueBear White-Label Tenant Activation Record',
    before: '<h2>A Practical Diagnostic Checklist',
    html: tenantOnboarding,
  },
  'white-label-ai-platform-unit-economics': {
    heading: 'BlueBear White-Label Unit-Economics Worksheet',
    before: '<h2>Diagnosing Margin Erosion',
    html: whiteLabelEconomics,
  },
  'ai-agent-control-plane-explained': {
    heading: 'BlueBear Control-Plane Request Lifecycle',
    before: '<h2>Is an AI Agent Control Plane Right',
    html: controlPlaneLifecycle,
  },
  'byoc-vs-managed-ai-agent-hosting': {
    heading: 'BlueBear BYOC Responsibility and Cost Matrix',
    before: '<h2>A Practical Diagnostic Checklist',
    html: byocMatrix,
  },
  'production-ai-agent-readiness-checklist': {
    heading: 'BlueBear Production Readiness Decision Record',
    before: '<h2>Diagnostic Checklist',
    html: readinessRecord,
  },
  'migrate-ai-agent-pilots-shared-platform': {
    heading: 'BlueBear Pilot-to-Platform Dependency Map',
    before: '<h2>A Practical Diagnostic Checklist',
    html: migrationMap,
  },
  'ai-agent-proof-of-value-plan': {
    heading: 'BlueBear Production Review Evidence Pack',
    before: '<h2>A Practical Diagnostic Checklist for Your AI Agent Proof</h2>',
    html: proofOfValue,
  },
  'establish-ai-agent-cost-baseline': {
    heading: 'BlueBear Cost-per-Outcome Baseline Worksheet',
    before: '<h2>Practical Diagnostic Checklist: Steps to Audit Your AI Agent Spending</h2>',
    html: costBaseline,
  },
  'llm-routing-shadow-mode': {
    heading: 'BlueBear Shadow-Mode Routing Evidence Contract',
    before: '<h2>What shadow mode can and cannot tell you</h2>',
    html: shadowRoutingContract,
  },
  'did-your-router-honour-the-plan': {
    heading: 'BlueBear Route Outcome Plan-Honoured Contract',
    before: '<h2>What a usable outcome record contains</h2>',
    html: planHonouredContract,
  },
};
